import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, PlusCircle, Trash2, FileText, ArrowRight } from "lucide-react";
import { API } from "../lib/auth";
import { useAuth } from "../lib/AuthContext";
import ProfileEditor from "../components/account/ProfileEditor";
import PasswordSection from "../components/account/PasswordSection";

const MinhaContaPage = () => {
  const { user, loading, logout, setUser, unreadCount } = useAuth();
  const navigate = useNavigate();

  // Delete confirmation
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/me`, { withCredentials: true });
      setUser(null);
      toast.success("Conta eliminada");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error("Não foi possível eliminar a conta");
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (loading || !user) {
    return (
      <main className="min-h-screen bg-[#0E2452] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-[#FF5A00] border-t-transparent rounded-full animate-spin" />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0E2452] pt-32 pb-20 px-6 md:px-12" data-testid="minha-conta-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border-2 border-[#FF5A00]" />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[#FF5A00]/20 border-2 border-[#FF5A00] flex items-center justify-center text-[#FF5A00] font-display font-bold text-xl">
                {(user.name || user.email || "?")[0].toUpperCase()}
              </div>
            )}
            <div>
              <div className="section-label mb-1">// A sua conta</div>
              <h1 className="font-display font-bold text-2xl md:text-3xl uppercase text-white leading-tight">
                Olá, {user.name?.split(" ")[0] || "cliente"}
              </h1>
              <p className="text-white/60 text-sm font-body">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/orcamento"
              data-testid="my-new-quote-btn"
              className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65100] text-white font-display font-bold uppercase text-xs tracking-wider px-5 py-3 rounded-full transition-all"
            >
              <PlusCircle className="w-4 h-4" />
              Novo pedido
            </Link>
            <button
              data-testid="customer-logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 border border-[#2F568F] text-white/80 hover:border-[#FF5A00] hover:text-white font-body text-sm px-4 py-3 rounded-full transition-all"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </div>

        <ProfileEditor user={user} setUser={setUser} />
        <PasswordSection />

        {/* Quotes CTA */}
        <Link
          to="/os-meus-orcamentos"
          data-testid="view-my-quotes-btn"
          className="group relative block rounded-2xl border-2 border-[#FF5A00] bg-gradient-to-r from-[#FF5A00]/20 via-[#FF5A00]/10 to-[#0B1D44] p-5 md:p-6 mb-6 overflow-hidden hover:from-[#FF5A00]/30 hover:via-[#FF5A00]/15 transition-all"
        >
          <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#FF5A00]/10 blur-2xl pointer-events-none" />
          <div className="relative flex flex-col md:flex-row md:items-center gap-4">
            <div className="w-14 h-14 shrink-0 rounded-2xl bg-[#FF5A00] flex items-center justify-center relative">
              <FileText className="w-7 h-7 text-white" />
              {unreadCount > 0 && (
                <span
                  data-testid="quotes-cta-unread-badge"
                  className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 border-2 border-[#0B1D44] text-white text-[10px] font-display font-bold flex items-center justify-center animate-pulse"
                >
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="section-label mb-1">// Orçamentos</div>
              <h2 className="font-display font-bold uppercase text-white text-xl leading-tight">Ver os meus orçamentos</h2>
              <p className="text-white/60 text-sm font-body mt-1">
                {unreadCount > 0
                  ? `Tem ${unreadCount} ${unreadCount === 1 ? "nova resposta" : "novas respostas"} da oficina à sua espera.`
                  : "Acompanhe os seus pedidos e veja as respostas da oficina."}
              </p>
            </div>
            <div className="shrink-0 inline-flex items-center gap-2 bg-[#FF5A00] group-hover:bg-[#E65100] text-white font-display font-bold uppercase text-xs tracking-wider px-5 py-3 rounded-full transition-all">
              Abrir
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </Link>


        {/* Danger zone */}
        <section
          data-testid="danger-zone"
          className="rounded-2xl border border-red-500/30 bg-red-950/10 p-5 md:p-6 mt-4"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-red-300 font-display font-semibold mb-1">// Zona sensível</div>
              <h3 className="font-display font-bold uppercase text-white text-base">Eliminar conta</h3>
              <p className="text-white/60 font-body text-sm mt-1">
                Remove permanentemente a sua conta. Os pedidos já enviados continuam registados na oficina para efeitos de histórico.
              </p>
            </div>
            {!confirmDelete ? (
              <button
                data-testid="delete-account-btn"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-2 border border-red-500/50 text-red-300 hover:bg-red-500/10 hover:border-red-500 font-display font-bold uppercase text-[11px] tracking-wider px-4 py-2.5 rounded-full transition-all shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Eliminar conta
              </button>
            ) : (
              <div className="flex gap-2 shrink-0">
                <button
                  data-testid="cancel-delete-btn"
                  onClick={() => setConfirmDelete(false)}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 border border-[#2F568F] text-white/80 font-body text-sm px-3 py-2.5 rounded-full transition-all disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  data-testid="confirm-delete-btn"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-display font-bold uppercase text-[11px] tracking-wider px-4 py-2.5 rounded-full transition-all disabled:opacity-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {deleting ? "A eliminar..." : "Confirmar eliminação"}
                </button>
              </div>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default MinhaContaPage;
